import { AlertTriangle, Check, Trash2, WandSparkles } from "lucide-react"
import { useEffect, useMemo, useRef } from "react"

import type { CustomThemeId } from "@/styles/themes/custom-themes"

import { EmptyState } from "@/components/shared/empty-state"
import { StatusAlert } from "@/components/shared/status-alert"
import { Tag } from "@/components/shared/tag"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import {
  createThemeBuilderPageStore,
  type ColorSection,
  useThemeBuilderState,
} from "@/lib/stores/theme-builder-page-store"
import { useActiveThemeId, useThemeCatalogVersion } from "@/lib/theme-store"
import { cn } from "@/lib/utils"
import {
  applyTheme,
  applyThemeSeed,
  createCustomThemeFromShareCode,
  deleteCustomTheme,
  encodeThemeShareCode,
  getCustomThemes,
  getDefaultThemeId,
  getThemeLabel,
  getThemeSeedForBuilder,
  isThemeNameTaken,
  saveCustomTheme,
  type ThemeId,
  type ThemeSeed,
  validateThemeName,
} from "@/styles/themes"
import { oklch } from "@/styles/themes/utils"

type ColorField = "h" | "c" | "l"

const SECTION_META: Record<
  ColorSection,
  {
    label: string
    previewLabel: string
    description: string
  }
> = {
  base: {
    label: "Base",
    previewLabel: "Surface",
    description: "Backgrounds, panels, and the overall atmosphere.",
  },
  primary: {
    label: "Primary",
    previewLabel: "Action",
    description: "Buttons, links, and the main call to action.",
  },
  accent: {
    label: "Accent",
    previewLabel: "Highlight",
    description: "Badges, highlights, and secondary moments.",
  },
}

const FIELD_META: Record<
  ColorField,
  {
    label: string
    min: number
    max: number
    step: number
    format: (value: number) => string
  }
> = {
  h: {
    label: "Hue",
    min: 0,
    max: 360,
    step: 1,
    format: (value) => `${Math.round(value)}°`,
  },
  c: {
    label: "Vividness",
    min: 0,
    max: 0.4,
    step: 0.01,
    format: (value) => value.toFixed(2),
  },
  l: {
    label: "Lightness",
    min: 0,
    max: 1,
    step: 0.01,
    format: (value) => value.toFixed(2),
  },
}

function clampValue(field: ColorField, value: number): number {
  if (!Number.isFinite(value)) return 0
  if (field === "h") return Math.min(360, Math.max(0, value))
  if (field === "c") return Math.min(0.4, Math.max(0, value))
  return Math.min(1, Math.max(0, value))
}

function parseNumeric(value: string, fallback: number): number {
  const parsed = Number.parseFloat(value)
  if (!Number.isFinite(parsed)) return fallback
  return parsed
}

function fieldError(field: ColorField, value: number): string | null {
  if (!Number.isFinite(value)) return "Must be a number"
  if (field === "h" && (value < 0 || value > 360)) return "Hue must be 0-360"
  if (field === "c" && (value < 0 || value > 0.4))
    return "Vividness must be 0-0.4"
  if (field === "l" && (value < 0 || value > 1)) return "Lightness must be 0-1"
  return null
}

function getSectionSwatch(seed: ThemeSeed, section: ColorSection): string {
  const color = seed[section]
  return oklch(color.l, color.c, color.h)
}

function getSectionTone(seed: ThemeSeed, section: ColorSection): string {
  const color = seed[section]
  const toneL =
    seed.mode === "dark"
      ? Math.min(0.96, color.l + 0.18)
      : Math.max(0.12, color.l - 0.22)
  const toneC = Math.max(0.01, color.c * 0.5)
  return oklch(toneL, toneC, color.h)
}

function getRangeTrack(
  seed: ThemeSeed,
  section: ColorSection,
  field: ColorField,
): string {
  const color = seed[section]

  if (field === "h") {
    return "linear-gradient(90deg, oklch(0.72 0.18 0), oklch(0.72 0.18 60), oklch(0.72 0.18 120), oklch(0.72 0.18 180), oklch(0.72 0.18 240), oklch(0.72 0.18 300), oklch(0.72 0.18 360))"
  }

  if (field === "c") {
    return `linear-gradient(90deg, ${oklch(color.l, 0, color.h)}, ${oklch(color.l, 0.4, color.h)})`
  }

  return `linear-gradient(90deg, ${oklch(0.02, color.c, color.h)}, ${oklch(0.98, color.c, color.h)})`
}

function getSurfaceWarnings(seed: ThemeSeed): string[] {
  const foregroundL = seed.mode === "dark" ? 0.95 : 0.255
  const primaryFgL = seed.mode === "dark" ? seed.base.l + 0.005 : 0.985
  const warnings: string[] = []

  if (Math.abs(seed.base.l - foregroundL) < 0.48) {
    warnings.push("Base and text may feel too close in contrast.")
  }

  if (Math.abs(seed.primary.l - primaryFgL) < 0.34) {
    warnings.push("Primary buttons may lose readability at this lightness.")
  }

  if (seed.accent.c > 0.32 && seed.accent.l > 0.8) {
    warnings.push("Accent highlights are getting very bright and vivid.")
  }

  return warnings
}

export function ThemeBuilderPage({ className }: { className?: string }) {
  const activeThemeId = useActiveThemeId()
  useThemeCatalogVersion()

  const storeRef = useRef(createThemeBuilderPageStore(activeThemeId))
  const store = storeRef.current
  const editingThemeId = useThemeBuilderState(store, (s) => s.editingThemeId)
  const activeSection = useThemeBuilderState(store, (s) => s.activeSection)
  const draft = useThemeBuilderState(store, (s) => s.draft)
  const shareCode = useThemeBuilderState(store, (s) => s.shareCode)
  const importCode = useThemeBuilderState(store, (s) => s.importCode)
  const importLabel = useThemeBuilderState(store, (s) => s.importLabel)
  const shareMessage = useThemeBuilderState(store, (s) => s.shareMessage)
  const importError = useThemeBuilderState(store, (s) => s.importError)
  const isSaving = useThemeBuilderState(store, (s) => s.isSaving)
  const saveError = useThemeBuilderState(store, (s) => s.saveError)

  const customThemes = getCustomThemes()

  useEffect(() => {
    applyThemeSeed(draft)
  }, [draft])

  useEffect(() => {
    store.getState().hydrateDraft(activeThemeId)
  }, [activeThemeId, store])

  const nameError = useMemo(() => {
    const nameRule = validateThemeName(draft.label)
    if (nameRule) return nameRule

    const taken = isThemeNameTaken(draft.label, editingThemeId ?? undefined)
    if (taken) return "Theme name must be unique"

    return null
  }, [draft.label, editingThemeId])

  const rangeErrors = useMemo(() => {
    const checks: Array<[ColorSection, ColorField]> = [
      ["base", "h"],
      ["base", "c"],
      ["base", "l"],
      ["primary", "h"],
      ["primary", "c"],
      ["primary", "l"],
      ["accent", "h"],
      ["accent", "c"],
      ["accent", "l"],
    ]

    return checks
      .map(([section, field]) => fieldError(field, draft[section][field]))
      .filter((value): value is string => Boolean(value))
  }, [draft])

  const warnings = useMemo(() => getSurfaceWarnings(draft), [draft])
  const saveDisabled = Boolean(nameError) || rangeErrors.length > 0 || isSaving
  const activeColor = draft[activeSection]
  const activeSectionMeta = SECTION_META[activeSection]

  const hydrateDraft = (themeId: ThemeId) =>
    store.getState().hydrateDraft(themeId)

  const setColorField = (
    section: ColorSection,
    field: ColorField,
    value: number,
  ) => {
    store.getState().setActiveSection(section)
    store.getState().setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: value,
      },
    }))
  }

  const clampField = (section: ColorSection, field: ColorField) => {
    store.getState().setDraft((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: clampValue(field, current[section][field]),
      },
    }))
  }

  const handleSave = () => {
    if (saveDisabled || store.getState().isSaving) return

    store.getState().setIsSaving(true)
    store.getState().setSaveError(null)

    try {
      const { id: _draftId, ...draftWithoutId } = draft

      const saved = editingThemeId
        ? saveCustomTheme({
            ...draftWithoutId,
            id: editingThemeId,
            label: draft.label.trim(),
          })
        : saveCustomTheme({
            ...draftWithoutId,
            label: draft.label.trim(),
          })

      store.getState().setEditingThemeId(saved.id)
      store.getState().setDraft(getThemeSeedForBuilder(saved.id))
      applyTheme(saved.id)
    } catch {
      store
        .getState()
        .setSaveError(
          "Failed to save theme. Storage may be full or unavailable.",
        )
    } finally {
      store.getState().setIsSaving(false)
    }
  }

  const handleDelete = (themeId: CustomThemeId) => {
    try {
      deleteCustomTheme(themeId)
    } catch {
      store
        .getState()
        .setSaveError("Failed to delete theme. Storage may be unavailable.")
      return
    }

    if (editingThemeId === themeId) {
      store.getState().setEditingThemeId(null)
      const fallback = getDefaultThemeId()
      store.getState().setDraft(getThemeSeedForBuilder(fallback))
      applyTheme(fallback)
    }
  }

  const handleResetDraft = () => {
    hydrateDraft(editingThemeId ?? activeThemeId)
  }

  const handleResetDefault = () => {
    const fallback = getDefaultThemeId()
    applyTheme(fallback)
    hydrateDraft(fallback)
  }

  const handleCopyShareCode = async () => {
    const code = encodeThemeShareCode(draft)
    store.getState().setShareCode(code)

    if (!globalThis.navigator?.clipboard) {
      store
        .getState()
        .setShareMessage("Share code generated. Copy it manually.")
      return
    }

    try {
      await globalThis.navigator.clipboard.writeText(code)
      store.getState().setShareMessage("Share code copied.")
    } catch {
      store
        .getState()
        .setShareMessage("Share code generated. Copy it manually.")
    }
  }

  const getImportErrorMessage = (error: string): string => {
    switch (error) {
      case "EMPTY":
        return "Paste a theme code first."
      case "INVALID_PREFIX":
      case "INVALID_VERSION":
        return "Theme code prefix is invalid."
      case "INVALID_CHECKSUM":
        return "Theme code failed integrity check."
      case "INVALID_LENGTH":
      case "INVALID_FORMAT":
      case "INVALID_PAYLOAD":
      default:
        return "Theme code is invalid."
    }
  }

  const handleImportCode = () => {
    try {
      const result = createCustomThemeFromShareCode(importCode, importLabel)
      if (!result.ok) {
        store.getState().setImportError(getImportErrorMessage(result.error))
        return
      }

      store.getState().setImportError(null)
      store.getState().setEditingThemeId(result.theme.id)
      store.getState().setDraft(getThemeSeedForBuilder(result.theme.id))
      applyTheme(result.theme.id)
      store.getState().setImportCode("")
    } catch {
      store.getState().setImportError("Failed to import theme.")
    }
  }

  return (
    <div className={cn("grid gap-3", className)}>
      <div className="overflow-hidden rounded-[1.3rem] border border-border/60 bg-[linear-gradient(135deg,color-mix(in_oklch,var(--primary)_8%,var(--card)),color-mix(in_oklch,var(--accent)_12%,var(--background)))] p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="text-kicker text-[0.72rem] text-muted-foreground">
              Theme Builder
            </div>
            <div className="text-sm font-semibold">
              Shape the mood before you save it.
            </div>
            <div className="text-xs text-muted-foreground">
              Pick a color role, drag the sliders, then fine-tune the numbers.
            </div>
          </div>

          <Tag className="max-w-40 truncate border-border/60 bg-background/70 text-[0.68rem]">
            Active: {getThemeLabel(activeThemeId)}
          </Tag>
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-[1.3fr_0.7fr]">
          <div className="grid gap-1.5 rounded-[1.1rem] border border-border/55 bg-background/52 p-2.5">
            <div className="text-kicker text-[0.7rem] tracking-[0.16em] text-muted-foreground">
              Quick Preview
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(["base", "primary", "accent"] as const).map((section) => (
                <div
                  key={section}
                  className="rounded-[0.95rem] border border-white/10 p-2"
                  style={{
                    background: getSectionSwatch(draft, section),
                    color: getSectionTone(draft, section),
                  }}
                >
                  <div className="text-kicker text-[0.62rem] tracking-[0.14em]">
                    {SECTION_META[section].previewLabel}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-2 rounded-[1.1rem] border border-border/55 bg-background/52 p-2.5">
            <div className="grid gap-1.5">
              <Label htmlFor="theme-name">Theme name</Label>
              <Input
                id="theme-name"
                value={draft.label}
                onChange={(event) => {
                  const value = event.currentTarget.value
                  store.getState().setDraft((current) => ({
                    ...current,
                    label: value,
                  }))
                }}
                onBlur={() => {
                  store.getState().setDraft((current) => ({
                    ...current,
                    label: current.label.trim(),
                  }))
                }}
                className="h-9 rounded-xl"
              />
              {nameError && (
                <div className="text-xs text-danger">{nameError}</div>
              )}
            </div>

            {/* <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                size="sm"
                variant={draft.mode === "dark" ? "default" : "outline"}
                onClick={() => {
                  store.getState().setDraft((current) => ({
                    ...current,
                    mode: "dark",
                  }))
                }}
              >
                Dark
              </Button>
              <Button
                type="button"
                size="sm"
                variant={draft.mode === "light" ? "default" : "outline"}
                onClick={() => {
                  store.getState().setDraft((current) => ({
                    ...current,
                    mode: "light",
                  }))
                }}
              >
                Light
              </Button>
            </div> */}
          </div>
        </div>
      </div>

      <div className="grid gap-1.5">
        <Label className="text-xs">Custom themes</Label>
        {customThemes.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {customThemes.map((item) => (
              <Button
                key={item.id}
                type="button"
                size="xs"
                variant={editingThemeId === item.id ? "default" : "outline"}
                onClick={() => {
                  store.getState().setEditingThemeId(item.id)
                  store.getState().setDraft(getThemeSeedForBuilder(item.id))
                }}
                className="max-w-56"
              >
                <span className="truncate">{item.label}</span>
              </Button>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<WandSparkles className="size-5" />}
            title="No custom themes yet"
            description="Save your first theme to see it listed here."
            tone="primary"
            intensity="subtle"
            className="p-3"
          />
        )}
      </div>

      <div className="grid gap-2.5 md:gap-3">
        <div className="text-kicker text-[0.72rem] text-muted-foreground">
          Color Roles
        </div>
        <div className="grid gap-2.5 sm:grid-cols-3 md:gap-3">
          {(Object.keys(SECTION_META) as ColorSection[]).map((section) => {
            const meta = SECTION_META[section]
            const isActive = activeSection === section

            return (
              <button
                key={section}
                type="button"
                aria-pressed={isActive}
                onClick={() => {
                  store.getState().setActiveSection(section)
                }}
                className={cn(
                  "grid gap-2.5 rounded-[1.15rem] border p-2.5 text-left transition-all duration-200 md:gap-3 md:p-3",
                  isActive
                    ? "border-primary/35 bg-[linear-gradient(145deg,color-mix(in_oklch,var(--primary)_14%,var(--card)),color-mix(in_oklch,var(--accent)_8%,var(--background)))] shadow-[0_18px_34px_-26px_color-mix(in_oklch,var(--primary)_65%,transparent)]"
                    : "border-border/60 bg-card/55 hover:border-primary/25 hover:bg-card/75",
                )}
              >
                <div
                  className="h-21 rounded-[0.95rem] border border-white/10 p-2.5 md:h-24 md:p-3"
                  style={{
                    background: `linear-gradient(135deg, ${getSectionSwatch(draft, section)}, color-mix(in_oklch, ${getSectionSwatch(draft, section)} 58%, black))`,
                    color: getSectionTone(draft, section),
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-kicker text-[0.64rem] tracking-[0.16em] md:text-[0.68rem]">
                      {meta.previewLabel}
                    </div>
                    {isActive && <Check className="size-3.5" />}
                  </div>
                  <div className="mt-4 inline-flex rounded-full border border-current/18 bg-black/8 px-2 py-1 text-[0.68rem] font-semibold md:mt-5 md:text-[0.72rem]">
                    {meta.label}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold md:text-base">
                      {meta.label}
                    </div>
                    <div className="text-[0.68rem] text-muted-foreground md:text-xs">
                      H {Math.round(draft[section].h)} / C{" "}
                      {draft[section].c.toFixed(2)} / L{" "}
                      {draft[section].l.toFixed(2)}
                    </div>
                  </div>
                  <div className="text-[0.72rem] leading-relaxed text-muted-foreground md:text-[0.76rem]">
                    {meta.description}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div className="overflow-hidden rounded-[1.25rem] border border-border/60 bg-card/60">
        <div className="border-b border-border/55 px-3 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">
                {activeSectionMeta.label} Controls
              </div>
              <div className="text-xs text-muted-foreground">
                {activeSectionMeta.description}
              </div>
            </div>
            <div
              className="size-11 rounded-2xl border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
              style={{
                background: getSectionSwatch(draft, activeSection),
              }}
              aria-label={`${activeSectionMeta.label} color swatch`}
            />
          </div>
        </div>

        <div className="grid gap-3 p-3">
          {(["h", "c", "l"] as const).map((field) => {
            const meta = FIELD_META[field]
            const error = fieldError(field, activeColor[field])
            const fieldId = `${activeSection}-${field}`

            return (
              <div key={field} className="grid gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label
                    htmlFor={fieldId}
                    className="text-[0.78rem] font-semibold"
                  >
                    {meta.label}
                  </Label>
                  <div className="text-[0.72rem] font-bold text-muted-foreground">
                    {meta.format(activeColor[field])}
                  </div>
                </div>

                <div className="grid items-center gap-2 sm:grid-cols-[1fr_5.4rem]">
                  <div
                    className="relative h-9 overflow-hidden rounded-full border border-border/65"
                    style={{
                      background: getRangeTrack(draft, activeSection, field),
                    }}
                  >
                    <div className="text-kicker absolute inset-y-0 left-3 flex items-center text-[0.6rem] text-white/65">
                      {field.toUpperCase()}
                    </div>
                    <input
                      id={fieldId}
                      type="range"
                      min={meta.min}
                      max={meta.max}
                      step={meta.step}
                      value={activeColor[field]}
                      onChange={(event) => {
                        const value = event.currentTarget.value
                        const next = parseNumeric(value, activeColor[field])
                        setColorField(activeSection, field, next)
                      }}
                      className="absolute inset-0 size-full cursor-pointer appearance-none bg-transparent px-3 accent-white"
                    />
                  </div>

                  <Input
                    type="number"
                    step={meta.step}
                    value={activeColor[field]}
                    onChange={(event) => {
                      const value = event.currentTarget.value
                      const next = parseNumeric(value, activeColor[field])
                      setColorField(activeSection, field, next)
                    }}
                    onBlur={() => {
                      clampField(activeSection, field)
                    }}
                    className="h-9 [appearance:textfield] rounded-xl px-2.5 text-right text-xs font-semibold tabular-nums [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                </div>

                {error && (
                  <div className="text-[0.68rem] text-danger">{error}</div>
                )}
              </div>
            )
          })}

          <div className="grid gap-2 rounded-2xl border border-border/55 bg-background/55 p-2.5 sm:grid-cols-3">
            <div className="rounded-[0.95rem] border border-border/60 p-2">
              <div className="text-kicker text-[0.62rem] tracking-[0.16em] text-muted-foreground">
                Surface
              </div>
              <div className="mt-2 rounded-lg border border-border/50 p-2 text-[0.72rem] font-medium">
                Background depth
              </div>
            </div>
            <div className="rounded-[0.95rem] border border-border/60 p-2">
              <div className="text-kicker text-[0.62rem] tracking-[0.16em] text-muted-foreground">
                Button
              </div>
              <div
                className="mt-2 rounded-full px-2.5 py-1.5 text-center text-[0.72rem] font-semibold"
                style={{
                  background: getSectionSwatch(draft, "primary"),
                  color: getSectionTone(draft, "primary"),
                }}
              >
                Primary action
              </div>
            </div>
            <div className="rounded-[0.95rem] border border-border/60 p-2">
              <div className="text-kicker text-[0.62rem] tracking-[0.16em] text-muted-foreground">
                Badge
              </div>
              <div
                className="mt-2 inline-flex rounded-full px-2.5 py-1.5 text-[0.72rem] font-semibold"
                style={{
                  background: getSectionSwatch(draft, "accent"),
                  color: getSectionTone(draft, "accent"),
                }}
              >
                Accent pulse
              </div>
            </div>
          </div>

          {warnings.length > 0 && (
            <div
              className="rounded-2xl border border-warning/30 bg-warning/10 p-2.5 text-[0.74rem] text-warning-foreground"
              role="alert"
              aria-live="polite"
            >
              <div className="flex items-center gap-2 font-semibold">
                <AlertTriangle className="size-4 text-warning" />
                Contrast watch
              </div>
              <div className="mt-1.5 grid gap-1 text-warning-foreground/90">
                {warnings.map((warning) => (
                  <div key={warning}>{warning}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleResetDraft}
        >
          Reset draft
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleResetDefault}
        >
          Reset to default
        </Button>
        {editingThemeId && (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={() => {
              handleDelete(editingThemeId)
            }}
          >
            <Trash2 className="size-3.5" />
            Delete theme
          </Button>
        )}
      </div>

      <div className="inline-card grid gap-2 bg-card/55 p-2.5">
        <div className="text-kicker inline-flex items-center gap-2 text-[0.72rem] tracking-[0.16em] text-muted-foreground">
          <WandSparkles className="size-3.5" />
          Share & Import
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              void handleCopyShareCode()
            }}
          >
            Copy share code
          </Button>
          {shareMessage && (
            <div
              className="text-[0.68rem] text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              {shareMessage}
            </div>
          )}
        </div>
        {shareCode && (
          <Input
            value={shareCode}
            readOnly
            aria-label="Generated share code"
            className="h-9 rounded-xl"
          />
        )}
        <div className="grid gap-2 sm:grid-cols-[1.2fr_0.8fr_auto]">
          <Input
            value={importCode}
            onChange={(event) => {
              store.getState().setImportCode(event.currentTarget.value)
            }}
            placeholder="TH1-...."
            aria-label="Theme share code"
            className="h-9 rounded-xl"
          />
          <Input
            value={importLabel}
            onChange={(event) => {
              store.getState().setImportLabel(event.currentTarget.value)
            }}
            placeholder="Imported Theme"
            aria-label="Imported theme name"
            className="h-9 rounded-xl"
          />
          <Button type="button" size="sm" onClick={handleImportCode}>
            Import code
          </Button>
        </div>
        {importError && (
          <div className="text-[0.72rem] text-danger">{importError}</div>
        )}
      </div>

      {saveError && (
        <StatusAlert tone="danger" className="text-xs">
          {saveError}
        </StatusAlert>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-border/60 pt-2">
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={saveDisabled}
        >
          {isSaving && <Spinner className="size-3.5" />}
          {isSaving ? "Saving..." : "Save theme"}
        </Button>
      </div>
    </div>
  )
}
